// Resolve a Supabase storage path into a usable URL.
//
// Strategy:
//   1. If the offline flag is on AND the blob is in the audio cache,
//      return a freshly created object URL. Caller MUST pass the url
//      back to releaseResolvedUrl() when done to avoid leaks.
//   2. Otherwise fall back to a signed URL (network required).
//
// Returned object has a kind marker so callers can handle cleanup.

import { supabase } from '../supabase'
import { OFFLINE_ENABLED, CACHE_AUDIO } from '../../config/offline'
import { getBlob } from './blobCache'

export type ResolvedUrlKind = 'object' | 'signed' | null

export interface ResolvedUrl {
  url: string | null
  kind: ResolvedUrlKind
}

const SIGNED_URL_TTL_SECONDS = 300

export async function resolveAttachmentUrl(storagePath: string): Promise<ResolvedUrl> {
  if (OFFLINE_ENABLED) {
    const blob = await getBlob(CACHE_AUDIO, storagePath)
    if (blob) {
      return { url: URL.createObjectURL(blob), kind: 'object' }
    }
  }

  try {
    const { data, error } = await supabase.storage
      .from('song-files')
      .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS)

    if (error || !data?.signedUrl) {
      return { url: null, kind: null }
    }
    return { url: data.signedUrl, kind: 'signed' }
  } catch (err) {
    console.debug('[offline] resolveAttachmentUrl signed URL failed:', err)
    return { url: null, kind: null }
  }
}

// Must be called for any ResolvedUrl with kind === 'object' once the
// consumer is done with it. Signed URLs do not need cleanup.
export function releaseResolvedUrl(resolved: ResolvedUrl): void {
  if (resolved.kind === 'object' && resolved.url) {
    URL.revokeObjectURL(resolved.url)
  }
}

// Convenience: open the resolved URL in a new tab. For 'object' URLs
// we release after a short delay so the browser has time to pick it
// up before the handle dies.
export async function openAttachment(storagePath: string): Promise<boolean> {
  const resolved = await resolveAttachmentUrl(storagePath)
  if (!resolved.url) return false

  window.open(resolved.url, '_blank')

  if (resolved.kind === 'object') {
    setTimeout(() => releaseResolvedUrl(resolved), 10_000)
  }
  return true
}
